// @ts-strict-ignore
import { AfterViewInit, Directive, ElementRef, HostBinding, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LMFieldBaseComponent } from '@field/lm-field-base.component';
import { LMModel } from '@form/model';
import { LMModelField } from '@form/model';
import { LMModelChangedEvent } from '@form/model';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';

import { SohoFileUploadComponent } from 'ids-enterprise-ng';
import { DocumentResource } from '@app/resources';
import { LMUrlUtilService } from '@app/services';
import { LMDialogButtonKey } from '@app/utils';

import { LMXLFileUploadService } from '@services/file-upload/lm-xlfile-upload.service';

@Directive()
export class LMFileuploadBaseComponent extends LMFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(SohoFileUploadComponent) fileUpload: SohoFileUploadComponent;

  @HostBinding('attr.data-automation-id') dataFieldAutomationId = '';

  @Input() layoutObject: LMFileUploadLayout;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;

  public originalValue = '';
  private ignoreDirtyEvent = false;
  private needsDirtyCheck = false;
  private _docTitleField: LMModelField;
  private _mimeTypeField: LMModelField;
  private _mimeType = '';
  public deleteAttachmentTitle = '';
  public isInAccessibilityMode: boolean;
  public url: string;
  public isEmpty = false;
  public isXLUpload = false;
  public isXLUploadComplete = false;
  public xlUploadStatus: string;
  public xlUploadTagStatus: string;
  public canCancelXLUpload = false;
  public canRetryXLUpload = false;
  public cancelXLUploadTitle: string;
  public retryXLUploadTitle: string;

  public getUrl(): string {
    if (!this.modelField?.value) {
      return '';
    }

    const dataView = this.model.getData();
    const resourceId = this.field.documentResourceId;
    const resource = new DocumentResource({ resourceId, dataView }, '');

    let params = {} as any;
    if (this.model.action) {
      params.action = this.model.action;
    }
    if (this.model.formSpec.busFormSpec.name) {
      params.form = this.model.formSpec.busFormSpec.name;
    }

    const formUrlInfo = this.model.formUrlInfo;
    if ((dataView.processFlowActions && dataView.processFlowActions.length) || formUrlInfo.processFlow) {
      params = Object.assign(params, formUrlInfo.navigationContext);
      params.redirect = true;
    }
    if (formUrlInfo.sourceResource) {
      params[ formUrlInfo.sourceResource.resourceType ] = formUrlInfo.sourceResource.resourceId;
    }

    return this.lmUrlUtilService.buildUrl({ resource, params });
  }

  constructor(
    protected lmFieldServiceAdapter: LMFieldServiceAdapter,
    protected lmUrlUtilService: LMUrlUtilService,
    protected lmXLFileUploadService: LMXLFileUploadService,
    protected elementRef: ElementRef,
  ) {
    super(lmFieldServiceAdapter);
    this.deleteAttachmentTitle = Soho.Locale.translate('DeleteAttachment');

    // a unique value is required for original value so the field will always display as dirty
    this.originalValue = this.id + '-' + this.bindId;
  }

  // events
  ngOnInit() {
    super.ngOnInit();
    this.isInAccessibilityMode = this.lmFieldServiceAdapter.lmGlobalPersonalizationService.getAccessibilityMode();
    this.initialize(this.layoutObject, this.specFields, this.model);
    this.url = this.getUrl();

    this.isXLUpload = this.modelField.xlUpload;
    this.cancelXLUploadTitle = Soho.Locale.translate('Cancel');
    this.retryXLUploadTitle = Soho.Locale.translate('Retry');

    this.dataFieldAutomationId = `lm-${this.fieldAutomationId}-field`;

    // when record changes, update the controls
    this.subscriptionArray.push(this.model.modelChanged
      .subscribe((modelChangedEvent: LMModelChangedEvent) => {
        if (this.fileUpload && modelChangedEvent.eventType === 'set') {
          // clear the files array if the new modelField.value is blank.
          // fileUpload.clearUploadFile() will trigger a change event which is caught with an onDirty event
          // and needs to be ignored at this point
          if (modelChangedEvent.source?.type === 'paging' && this.lmFieldServiceAdapter.lmFieldUtilService.isBlank(this.specField.type, this.modelField.value)) {
            this.ignoreDirtyEvent = true;
            this.fileUpload.clearUploadFile();
            this.ignoreDirtyEvent = false;
          } else if (modelChangedEvent.source?.type === 'transaction' || modelChangedEvent.source?.type === 'refresh') {
            // means form is saved or form was refreshed so its no longer dirty, need this so that
            // it would properly transition to dirty after clearing it again
            super.onPristine();
          }
        }

        // initialize the trackdirty control so it gets current information
        if (this.trackDirtyChildren.length > 0) {
          this.trackDirtyChildren.first.updated();
        }

        this.url = this.getUrl();
      })
    );

    this.subscriptionArray.push(this.modelField.valueChanged
    .subscribe(() => {
      this.initializeXLUpload();
      this.checkEmpty();
    }));
    if (!this.displayAsText) {
      this.subscriptionArray.push(this.readOnly$.subscribe((value: boolean) => {
        setTimeout(() => {
          if (this.fileUpload && value) {
            // should be read only
            this.fileUpload.readonly = true;
          }
        }, 1);
      }));
    }
  }

  private initializeXLUpload() {
    if (this.isXLUpload) {
      this.xlUploadStatus = this.modelField.xlUploadStatus;
      this.isXLUploadComplete = this.modelField.xlUploadStatus === undefined || this.modelField.xlUploadStatus === 'completed';
      this.canCancelXLUpload = (this.modelField.xlUploadStatus === 'initiated-inprogress' || this.modelField.xlUploadStatus === 'inprogress');
      this.canRetryXLUpload = this.modelField.xlUploadStatus === 'error';
      this.xlUploadTagStatus = this.canCancelXLUpload ? Soho.Locale.translate('XLUploadStatusInprogress')
        : (this.canRetryXLUpload) ? this.modelField.xlUploadError : '';
    }
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    //LMCLIENT-36048 need to make the soho-fileupload readonly input not tabbable when in power user mode
    if(!this.isInAccessibilityMode && this.isReadOnly) {
      this.elementRef?.nativeElement?.querySelector('input.fileupload')?.setAttribute('tabindex', '-1');
    }

    if (this.fileUpload && this.isReadOnly) {
      this.fileUpload.readonly = this.isReadOnly;
    }

    if (this.modelField.docTitleFieldId) {
      this._docTitleField = this.model.getField(this.modelField.docTitleFieldId);
    }

    if (this.modelField.mimeTypeFieldId) {
      this._mimeTypeField = this.model.getField(this.modelField.mimeTypeFieldId);

      if (this.modelField.mimeType) {
        this._mimeType = this.modelField.mimeType;
      }
    }

    this.checkEmpty();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  private clearFile() {
    this.modelField.multiPartFile = undefined;
    this.modelField.value = '';

    if (this._docTitleField) {
      this._docTitleField.value = '';
    }

    if (this._mimeTypeField) {
      this._mimeType = '';
      this._mimeTypeField.value = this._mimeType;
    }
  }

  onDirty(event) {
    if (this.ignoreDirtyEvent) {
      return;
    }

    super.onDirty(event);

    if (event?.currentTarget) {
      const files = event.currentTarget.files;

      // get the filename from the element
      if (files && files.length > 0) {
        const hasError = files[ 0 ].size <= 0 || (this.modelField.xlUpload && files[ 0 ].size > this.modelField.maxFileSize);
        const errorMsg = (hasError && files[ 0 ].size <= 0 ) ? Soho.Locale.translate('CannotSelectAnEmptyFile') : Soho.Locale.translate('FileSizeGreaterThanMax');
        if (hasError) {
          this.showErrorMessage(errorMsg);
          if (this.fileUpload) {
            this.fileUpload.clearUploadFile();
          }
        } else {
          this.modelField.multiPartFile = files[ 0 ];
          const file = this.modelField.multiPartFile as File;
          if (this._docTitleField) {
            this._docTitleField.value = file.name;
          }

          if (this._mimeTypeField) {
            this._mimeType = this.modelField.multiPartFile.type;
            this._mimeTypeField.value = this._mimeType;
          }
        }
      } else if (!this.needsDirtyCheck) {
        // a clear happened, skip this if it
        // originated from removeAttachment
        this.clearFile();
      }
    }
    this.handleOnFieldChanged();
  }

  private showErrorMessage(errorMsg:string) {
    const dialogId = 'FileUpload';
    this.lmFieldServiceAdapter.lmModalWrapperService
    .message()
    .message(errorMsg)
    .title('')
    .buttons([
      {
        id: this.lmFieldServiceAdapter.lmUniqueIdService.uniqueId([ this.id, dialogId, LMDialogButtonKey.CLOSE ]),
        text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
        click: (e, modal) => {
          modal.close();
        }
      }
    ])
    .open();
  }

  onPristine() {
    // ---------------------------------------------------------------------------
    // NOTE: When you attach a file on a blank file upload field, modelField.value
    // remains empty until form was saved. However, modelField.multiPartFile will
    // be set and so we need to clear it here if the field is cleared and goes
    // back to empty/pristine state which won't be caught by onDirty handler above
    // ---------------------------------------------------------------------------
    if (!this.modelField.value && this.modelField.multiPartFile) {
      this.clearFile();
    }
    super.onPristine();
  }

  contextMenuClicked = ($event) => {
    this.onContextMenuEvent($event);
  };

  public cancelXLUpload() {
    this.lmXLFileUploadService.abortUpload(this.modelField.xlUploadId).subscribe({
      next: (aborted: boolean) => {
        if (aborted) {
          this.modelField.xlUploadStatus = undefined;
          this.modelField.xlUploadId = undefined;
          this.clearFile();
          this.initializeXLUpload();
          this.checkEmpty();
          // if(this.fileUpload) {
          //   this.fileUpload.clearUploadFile();
          // }
        } else {
          this.showErrorMessage(Soho.Locale.translate('FileAbortUploadFailure'));
        }
      }
    });
  }

  public retryXLUpload() {
    this.modelField.xlUploadStatus = undefined;
    this.modelField.xlUploadId = undefined;
    this.clearFile();
    this.initializeXLUpload();
    this.checkEmpty();
  }

  public removeAttachment() {
    this.clearFile();
    this.needsDirtyCheck = true;
    this.onDirty(null);
  }

  private checkEmpty(): void {
    this.isEmpty = !this.modelField.value;
    if (this.isEmpty && !!this.modelField.originalValue && this.trackDirtyChildren?.length && this.needsDirtyCheck) {
      this.trackDirtyChildren.first.changeDirty();
      this.needsDirtyCheck = false;
    }
  }

  onFileUploadChange(event: SohoFileUploadEvent) {
    if(!event.currentTarget.value) {
      // no value means that the field has been emptied from the x button or deleting the value. Make sure the model
      // field is correctly updated. This is only called from the input part of the file upload.
      this.removeAttachment();
    }
  }
}
